import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapPin, Phone, Clock, MessageCircle } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { branches } from '@/data/branches';
import { useLanguage } from '@/hooks/useLanguage';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const Branches = () => {
  const { t } = useContent();
  const { language } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Branches - ElhamyLabs';
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('common.branches')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('branches.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Branches Grid */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {branches.map((branch, index) => (
              <ScrollReveal key={branch.id} delay={index * 0.1}>
                <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <h3 className="text-2xl font-bold mb-4 text-light-text dark:text-dark-text">
                    {branch.name[language]}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin size={20} className="text-brand-orange-base flex-shrink-0 mt-1" />
                      <p className="text-light-text-secondary dark:text-dark-text-secondary">
                        {branch.address[language]}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Phone size={20} className="text-brand-purple-base dark:text-brand-orange-base" />
                      <a
                        href={`tel:${branch.phone}`}
                        className="text-light-text-secondary dark:text-dark-text-secondary hover:text-brand-purple-base dark:hover:text-brand-orange-base transition-colors"
                        dir="ltr"
                      >
                        {branch.phone}
                      </a>
                    </div>

                    <div className="flex items-center gap-3">
                      <MessageCircle size={20} className="text-brand-purple-base dark:text-brand-orange-base" />
                      <a
                        href={`https://wa.me/${branch.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-light-text-secondary dark:text-dark-text-secondary hover:text-brand-purple-base dark:hover:text-brand-orange-base transition-colors"
                      >
                        {t('branches.whatsapp')}
                      </a>
                    </div>

                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-brand-purple-base dark:text-brand-orange-base" />
                      <p className="text-light-text-secondary dark:text-dark-text-secondary">
                        {branch.hours[language]}
                      </p>
                    </div>
                  </div>

                  <button className="mt-6 w-full px-4 py-2 bg-brand-orange-base text-white rounded-lg hover:bg-brand-orange-light transition-colors">
                    {t('common.getDirections')}
                  </button>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Branches;

